import { useEffect, useRef, useMemo } from 'react';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import type { MinimalDocument } from '../types';
import type { Document as ApiDocument } from '../services/api';
import logger from '../utils/simpleLogger';

interface UseDocumentSelectionSyncOptions {
  selectedDocIds: string[];
  setSelectedDocuments: (ids: string[]) => void;
  documents: ApiDocument[];
}

/**
 * useDocumentSelectionSync - Keeps DocumentSelectionContext in sync with
 * useDocumentManagement, and provides derived computed values.
 *
 * Context is the single source of truth for document selection.
 * This hook syncs context -> management (one-way) and computes
 * selectedMinimalDocuments / availableMinimalDocuments.
 */
export function useDocumentSelectionSync({
  selectedDocIds,
  setSelectedDocuments,
  documents,
}: UseDocumentSelectionSyncOptions) {
  const {
    selectedIds: contextSelectedIds,
    selectedDocuments: contextSelectedDocuments,
    sources: contextSources,
    selectDocuments: contextSelectDocuments,
    deselectDocuments: contextDeselectDocuments,
  } = useDocumentSelection();

  // Use refs to prevent infinite loops (only one-way sync: context -> management)
  const isSyncingToManagementRef = useRef(false);
  const lastContextIdsRef = useRef<string[]>([]);

  // CONTEXT IS THE SINGLE SOURCE OF TRUTH
  useEffect(() => {
    if (isSyncingToManagementRef.current) return;

    const contextChanged =
      contextSelectedIds.length !== lastContextIdsRef.current.length ||
      !contextSelectedIds.every((id) => lastContextIdsRef.current.includes(id));

    if (contextChanged) {
      const needsUpdate =
        contextSelectedIds.length !== selectedDocIds.length ||
        !contextSelectedIds.every((id) => selectedDocIds.includes(id));

      if (needsUpdate) {
        isSyncingToManagementRef.current = true;
        lastContextIdsRef.current = [...contextSelectedIds];
        logger.debug('[MainLayout] Syncing context to useDocumentManagement (read-only sync)', {
          contextIds: contextSelectedIds,
          currentManagementIds: selectedDocIds,
        });
        setSelectedDocuments(contextSelectedIds);
        queueMicrotask(() => {
          isSyncingToManagementRef.current = false;
        });
      } else {
        lastContextIdsRef.current = [...contextSelectedIds];
      }
    }
  }, [contextSelectedIds, selectedDocIds, setSelectedDocuments]);

  const effectiveSelectedIds = contextSelectedIds;

  // Enhanced selectedMinimalDocuments that uses context documents (includes shared)
  const selectedMinimalDocuments = useMemo<MinimalDocument[]>(() => {
    return effectiveSelectedIds
      .map((id: string) => {
        const contextDoc = contextSelectedDocuments.find((d) => d.id === id);
        if (contextDoc) return contextDoc;
        const doc = documents.find((d) => d.id === id);
        if (!doc) return null;
        return {
          id: doc.id,
          name: doc.name,
          documentType: doc.file_type || 'other',
          scope: (doc.scope as 'private' | 'shared') || 'private',
          original_filename: doc.original_filename,
        } as MinimalDocument;
      })
      .filter((doc): doc is MinimalDocument => doc !== null);
  }, [effectiveSelectedIds, contextSelectedDocuments, documents]);

  // All documents as minimal documents for the dropdown
  const availableMinimalDocuments = useMemo<MinimalDocument[]>(() => {
    const privateDocs = documents.map(
      (doc: ApiDocument) =>
        ({
          id: doc.id,
          name: doc.name,
          documentType: doc.file_type || 'other',
          scope: (doc.scope as 'private' | 'shared') || 'private',
          original_filename: doc.original_filename,
          company_name: doc.company_name,
        }) as MinimalDocument
    );

    const contextDocIds = new Set(privateDocs.map((d) => d.id));
    const sharedDocsFromContext = contextSelectedDocuments
      .filter((doc) => !contextDocIds.has(doc.id))
      .map(
        (doc) =>
          ({
            id: doc.id,
            name: doc.name,
            documentType: doc.documentType || 'other',
            scope: doc.scope || 'private',
            original_filename: doc.original_filename,
            company_name: doc.company_name,
          }) as MinimalDocument
      );

    return [...privateDocs, ...sharedDocsFromContext];
  }, [documents, contextSelectedDocuments]);

  return {
    effectiveSelectedIds,
    selectedMinimalDocuments,
    availableMinimalDocuments,
    contextSelectedIds,
    contextSources,
    contextSelectDocuments,
    contextDeselectDocuments,
  };
}
